import { createEnv } from '@t3-oss/env-nextjs'
import { z } from 'zod'

export const env = createEnv({
  server: {
    SESSION_SECRET_KEY: z.string().min(1),
    SERVER_REQUESTS_SECRET_KEY: z.string().min(1),

    KRAKEND_URL: z.string().url(),
    KRAKEND_API_KEY: z.string().min(1),
    KRAKEND_API_VERSION: z.string().min(1),

    CONTENTFUL_SPACE_ID: z.string().min(1),
    CONTENTFUL_ACCESS_TOKEN: z.string().min(1),
    CONTENTFUL_PREVIEW_ACCESS_TOKEN: z.string().min(1),
    CONTENTFUL_PREVIEW_SECRET: z.string().min(1),
    CONTENTFUL_ENVIRONMENT: z.string().default('master'),

    // Shared secret for Contentful webhook -> /api/revalidate. Sent by the
    // webhook in the `x-vercel-revalidation-key` header.
    REVALIDATION_SECRET: z.string().min(1),

    // SHOPIFY_STORE_DOMAIN and SHOPIFY_STOREFRONT_API_VERSION are only read by
    // .graphqlrc.ts during codegen, so leave optional - runtime uses the NEXT_PUBLIC_ versions.
    SHOPIFY_STORE_DOMAIN: z.string().optional(),
    SHOPIFY_STOREFRONT_API_VERSION: z.string().optional(),
    SHOPIFY_PRIVATE_STOREFRONT_TOKEN: z.string().min(1),
    SHOPIFY_PRIVATE_ADMIN_API_TOKEN: z.string().min(1),
    SHOPIFY_ADMIN_API_VERSION: z.string().min(1),
    SHOPIFY_CUSTOMER_ACCOUNT_API_VERSION: z.string().min(1),

    STRIPE_SECRET_KEY: z.string().optional(),
    DEVELOPMENT_AUTH_REDIRECT_PROXY_URL: z.string().url().optional(),

    // Auto-injected by Vercel on deployments. Undefined locally.
    VERCEL_ENV: z.enum(['production', 'preview', 'development']).optional(),
  },

  client: {
    NEXT_PUBLIC_BASE_URL: z.string().url(),

    NEXT_PUBLIC_SHOPIFY_STORE_DOMAIN: z.string().min(1),
    NEXT_PUBLIC_SHOPIFY_STOREFRONT_API_VERSION: z.string().min(1),
    NEXT_PUBLIC_SHOPIFY_STOREFRONT_TOKEN: z.string().min(1),
    NEXT_PUBLIC_SHOPIFY_SHOP_ID: z.string().min(1),
    NEXT_PUBLIC_SHOPIFY_CLIENT_ID: z.string().min(1),
    NEXT_PUBLIC_SHOPIFY_REDIRECT_URI_ENDPOINT: z.string().min(1),

    NEXT_PUBLIC_ALGOLIA_APP_ID: z.string().min(1),
    NEXT_PUBLIC_ALGOLIA_SEARCH_API_KEY: z.string().min(1),

    // Aliased from CONTENTFUL_SPACE_ID / CONTENTFUL_ENVIRONMENT via next.config.ts `env:` block.
    NEXT_PUBLIC_CONTENTFUL_SPACE_ID: z.string().min(1),
    NEXT_PUBLIC_CONTENTFUL_ENVIRONMENT: z.string().default('master'),
    NEXT_PUBLIC_HEADER_ID: z.string().min(1),
    NEXT_PUBLIC_FOOTER_ID: z.string().min(1),

    NEXT_PUBLIC_FORM_IO_PROJECT_URL: z.string().url(),
    NEXT_PUBLIC_FORM_IO_MOVING_FORM_URL: z.string().url(),
    NEXT_PUBLIC_FORM_IO_LPG_APPLIANCE: z.string().min(1),
    NEXT_PUBLIC_FORM_IO_NON_SERVICEABLE_ADDRESS: z.string().min(1),
    NEXT_PUBLIC_FORM_IO_PARTIAL_ONE_ON_BOARDING_FLOW_URL: z.string().min(1),
    NEXT_PUBLIC_FORM_IO_PARTIAL_TWO_ON_BOARDING_FLOW_URL: z.string().min(1),
    NEXT_PUBLIC_FORM_IO_PARTIAL_THREE_ON_BOARDING_FLOW_URL: z.string().min(1),
    NEXT_PUBLIC_FORM_IO_PARTIAL_FOUR_ON_BOARDING_FLOW_URL: z.string().min(1),
    NEXT_PUBLIC_FORM_IO_HEATER_ONBOARDING_FORM_ID: z.string().optional(),

    NEXT_PUBLIC_SETTINGS_ID: z.string().optional(),
    NEXT_PUBLIC_GOOGLE_MAP_API_KEY: z.string().optional(),
    NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID: z.string().optional(),
    NEXT_PUBLIC_WEBCHAT_ACCOUNT_ID: z.string().optional(),
    NEXT_PUBLIC_WEBCHAT_CHATNAME: z.string().optional(),
    NEXT_PUBLIC_ABN_LOOKUP_GUID: z.string().optional(),
    NEXT_PUBLIC_STOCKIST_WIDGET_TAG: z.string().optional(),
  },

  runtimeEnv: {
    SESSION_SECRET_KEY: process.env.SESSION_SECRET_KEY,
    SERVER_REQUESTS_SECRET_KEY: process.env.SERVER_REQUESTS_SECRET_KEY,
    KRAKEND_URL: process.env.KRAKEND_URL,
    KRAKEND_API_KEY: process.env.KRAKEND_API_KEY,
    KRAKEND_API_VERSION: process.env.KRAKEND_API_VERSION,
    CONTENTFUL_SPACE_ID: process.env.CONTENTFUL_SPACE_ID,
    CONTENTFUL_ACCESS_TOKEN: process.env.CONTENTFUL_ACCESS_TOKEN,
    CONTENTFUL_PREVIEW_ACCESS_TOKEN: process.env.CONTENTFUL_PREVIEW_ACCESS_TOKEN,
    CONTENTFUL_PREVIEW_SECRET: process.env.CONTENTFUL_PREVIEW_SECRET,
    CONTENTFUL_ENVIRONMENT: process.env.CONTENTFUL_ENVIRONMENT,
    REVALIDATION_SECRET: process.env.REVALIDATION_SECRET,
    SHOPIFY_STORE_DOMAIN: process.env.SHOPIFY_STORE_DOMAIN,
    SHOPIFY_PRIVATE_STOREFRONT_TOKEN: process.env.SHOPIFY_PRIVATE_STOREFRONT_TOKEN,
    SHOPIFY_PRIVATE_ADMIN_API_TOKEN: process.env.SHOPIFY_PRIVATE_ADMIN_API_TOKEN,
    SHOPIFY_STOREFRONT_API_VERSION: process.env.SHOPIFY_STOREFRONT_API_VERSION,
    SHOPIFY_ADMIN_API_VERSION: process.env.SHOPIFY_ADMIN_API_VERSION,
    SHOPIFY_CUSTOMER_ACCOUNT_API_VERSION: process.env.SHOPIFY_CUSTOMER_ACCOUNT_API_VERSION,
    STRIPE_SECRET_KEY: process.env.STRIPE_SECRET_KEY,
    DEVELOPMENT_AUTH_REDIRECT_PROXY_URL: process.env.DEVELOPMENT_AUTH_REDIRECT_PROXY_URL,
    VERCEL_ENV: process.env.VERCEL_ENV,

    NEXT_PUBLIC_BASE_URL: process.env.NEXT_PUBLIC_BASE_URL,
    NEXT_PUBLIC_SHOPIFY_STORE_DOMAIN: process.env.NEXT_PUBLIC_SHOPIFY_STORE_DOMAIN,
    NEXT_PUBLIC_SHOPIFY_STOREFRONT_API_VERSION: process.env.NEXT_PUBLIC_SHOPIFY_STOREFRONT_API_VERSION,
    NEXT_PUBLIC_SHOPIFY_STOREFRONT_TOKEN: process.env.NEXT_PUBLIC_SHOPIFY_STOREFRONT_TOKEN,
    NEXT_PUBLIC_SHOPIFY_SHOP_ID: process.env.NEXT_PUBLIC_SHOPIFY_SHOP_ID,
    NEXT_PUBLIC_SHOPIFY_CLIENT_ID: process.env.NEXT_PUBLIC_SHOPIFY_CLIENT_ID,
    NEXT_PUBLIC_SHOPIFY_REDIRECT_URI_ENDPOINT: process.env.NEXT_PUBLIC_SHOPIFY_REDIRECT_URI_ENDPOINT,
    NEXT_PUBLIC_ALGOLIA_APP_ID: process.env.NEXT_PUBLIC_ALGOLIA_APP_ID,
    NEXT_PUBLIC_ALGOLIA_SEARCH_API_KEY: process.env.NEXT_PUBLIC_ALGOLIA_SEARCH_API_KEY,
    // next.config.ts `env:` aliasing only feeds these into the client bundle, not the
    // server process.env. Fall back to the server-only twin so server-side validation passes.
    NEXT_PUBLIC_CONTENTFUL_SPACE_ID: process.env.NEXT_PUBLIC_CONTENTFUL_SPACE_ID ?? process.env.CONTENTFUL_SPACE_ID,
    NEXT_PUBLIC_CONTENTFUL_ENVIRONMENT: process.env.NEXT_PUBLIC_CONTENTFUL_ENVIRONMENT ?? process.env.CONTENTFUL_ENVIRONMENT,
    NEXT_PUBLIC_HEADER_ID: process.env.NEXT_PUBLIC_HEADER_ID,
    NEXT_PUBLIC_FOOTER_ID: process.env.NEXT_PUBLIC_FOOTER_ID,
    NEXT_PUBLIC_FORM_IO_PROJECT_URL: process.env.NEXT_PUBLIC_FORM_IO_PROJECT_URL,
    NEXT_PUBLIC_FORM_IO_MOVING_FORM_URL: process.env.NEXT_PUBLIC_FORM_IO_MOVING_FORM_URL,
    NEXT_PUBLIC_FORM_IO_LPG_APPLIANCE: process.env.NEXT_PUBLIC_FORM_IO_LPG_APPLIANCE,
    NEXT_PUBLIC_FORM_IO_NON_SERVICEABLE_ADDRESS: process.env.NEXT_PUBLIC_FORM_IO_NON_SERVICEABLE_ADDRESS,
    NEXT_PUBLIC_FORM_IO_HEATER_ONBOARDING_FORM_ID: process.env.NEXT_PUBLIC_FORM_IO_HEATER_ONBOARDING_FORM_ID,
    NEXT_PUBLIC_FORM_IO_PARTIAL_ONE_ON_BOARDING_FLOW_URL: process.env.NEXT_PUBLIC_FORM_IO_PARTIAL_ONE_ON_BOARDING_FLOW_URL,
    NEXT_PUBLIC_FORM_IO_PARTIAL_TWO_ON_BOARDING_FLOW_URL: process.env.NEXT_PUBLIC_FORM_IO_PARTIAL_TWO_ON_BOARDING_FLOW_URL,
    NEXT_PUBLIC_FORM_IO_PARTIAL_THREE_ON_BOARDING_FLOW_URL: process.env.NEXT_PUBLIC_FORM_IO_PARTIAL_THREE_ON_BOARDING_FLOW_URL,
    NEXT_PUBLIC_FORM_IO_PARTIAL_FOUR_ON_BOARDING_FLOW_URL: process.env.NEXT_PUBLIC_FORM_IO_PARTIAL_FOUR_ON_BOARDING_FLOW_URL,
    NEXT_PUBLIC_SETTINGS_ID: process.env.NEXT_PUBLIC_SETTINGS_ID,
    NEXT_PUBLIC_GOOGLE_MAP_API_KEY: process.env.NEXT_PUBLIC_GOOGLE_MAP_API_KEY,
    NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID: process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID,
    NEXT_PUBLIC_WEBCHAT_ACCOUNT_ID: process.env.NEXT_PUBLIC_WEBCHAT_ACCOUNT_ID,
    NEXT_PUBLIC_WEBCHAT_CHATNAME: process.env.NEXT_PUBLIC_WEBCHAT_CHATNAME,
    NEXT_PUBLIC_ABN_LOOKUP_GUID: process.env.NEXT_PUBLIC_ABN_LOOKUP_GUID,
    NEXT_PUBLIC_STOCKIST_WIDGET_TAG: process.env.NEXT_PUBLIC_STOCKIST_WIDGET_TAG,
  },

  skipValidation: !!process.env.SKIP_ENV_VALIDATION,
  emptyStringAsUndefined: true,
})
